/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable sonarjs/prefer-immediate-return */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/restrict-template-expressions */

import { Injectable } from '@angular/core'
import { UtilsService } from '../business/utils.service'
import { ApiService } from '../api/api.service'
import { BrandFileKeys, FileDto, UniqueFileKeys, UploadFileDto } from '../api/models/file.model'
import { CacheService } from '../business/cache.service'
import { CRBSettingKeys, DocumentLinkStatus, SettingsDocument } from '../api/models/configuration.model'
import { BrandColors, BrandTheme, ResolvedScope, cssVariablesOf, resolveBrandTheme } from './brand-theme.resolver'
import { environment } from '../../../../src/environments/environment'

export { BrandColors, BrandTheme, ResolvedScope, ThemeMode } from './brand-theme.resolver'

const THEME_STYLE_ELEMENT_ID = 'brand-theme'

@Injectable({
  providedIn: 'root'
})
export class BrandService {

  constructor(private apiService: ApiService,
    private cacheService: CacheService) {
  }

  async setBrand(): Promise<void> {
    await this.setFiles()
    await this.setColors()
  }

  get origin(): string {
    const origin = window.location.origin

    //local for testing
    if (origin.includes('localhost')) {
      return environment.cRBTestDomain
    }

    //stage env workaround
    if (origin.includes('stg-') && origin.includes('slpuat')) {
      const subdomain = origin
        .replace('https://', '')
        .replace('stg-', '')
        .replace('.slpuat.com', '')

      return `https://${subdomain}.viewaccount.com`
    }

    return origin
  }

  async setFiles(brandFiles: FileDto[] = null): Promise<void> {
    const files = brandFiles || await this.apiService.getBrandFiles(encodeURIComponent(this.origin))

    this.cacheService.setBrandFiles(files)

    if (files) {
      for (let i = 0; i < UniqueFileKeys.length; i++) {
        const brandFile = UniqueFileKeys[i]
        const apiFile = files.find(q => q.key == brandFile)
        if (apiFile) {
          document.documentElement.style.setProperty(`--${brandFile}`, `url('${apiFile.url}')`)
        } else {
          document.documentElement.style.setProperty(`--${brandFile}`, '')
        }
      }

      //marketing banner images
      UtilsService.marketingImages = files.filter(q => q.key == BrandFileKeys.MarketingBanner).map(q => q.url)

      //favicon
      UtilsService.favicon = files.find(q => q.key == BrandFileKeys.Favicon)?.url
      UtilsService.FaviconChangeEvent.emit()
    }
  }

  async setColors(): Promise<void> {
    this.applyTheme(await this.getBrandTheme())
  }

  /**
   * Writes the whole theme into a single managed style element, one block per mode.
   *
   * The values cannot be written as inline styles on the root element: an inline
   * declaration outranks any stylesheet rule, so a dark mode block would never win.
   */
  applyTheme(theme: BrandTheme): void {
    this.theme = theme

    const css = [
      `:root {\n${this.declarationsFor(theme.light)}\n}`,
      `body.dark {\n${this.declarationsFor(theme.dark)}\n}`,
    ].join('\n')

    let styleElement = document.getElementById(THEME_STYLE_ELEMENT_ID) as HTMLStyleElement

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = THEME_STYLE_ELEMENT_ID
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = css

    this.refreshForColorScheme()
  }

  /**
   * Recalculates the flags that pick the light or dark version of the logo. They depend on
   * the colours in use, so they have to be redone whenever the active mode changes.
   */
  refreshForColorScheme(): void {
    const active = this.darkModeActive ? this.theme?.dark : this.theme?.light

    if (!active) return

    UtilsService.brandedGlobalHeaderColorIsBrightness =
      this.getColorBrightness(active.elements[CRBSettingKeys.GlobalHeaderColor]) > 125

    UtilsService.brandedBottomNavigationColorIsBrightness =
      this.getColorBrightness(active.elements[CRBSettingKeys.BottomNavigationColor]) > 125
  }

  async getBrandFiles(domain: string = null, previewModel = false): Promise<FileDto[]> {
    return await this.apiService.getBrandFiles(domain || encodeURIComponent(this.origin), previewModel)
  }

  /** Resolved light mode element colours, for callers that only care about the default mode. */
  async getBrandColors(domain: string = null, previewModel = false): Promise<BrandColors> {
    return (await this.getBrandTheme(domain, previewModel)).light.elements
  }

  async getBrandTheme(domain: string = null, previewModel = false): Promise<BrandTheme> {
    const settings = await this.getSettings(domain, previewModel)

    return resolveBrandTheme(settings)
  }

  async getSettings(domain: string = null, previewModel = false): Promise<SettingsDocument> {
    const settings = await this.apiService.getBrandConfigSettings(domain || encodeURIComponent(this.origin), previewModel)
    this.cacheService.setBrandConfigSettings(settings)

    return settings
  }

  async uploadBrandFile(file: UploadFileDto): Promise<FileDto> {
    return this.apiService.uploadBrandFile(file)
  }

  async publishBrandConfiguration(crbConfigRId: number, programCode: string): Promise<boolean> {
    return this.apiService.publishBrandConfiguration(crbConfigRId, programCode)
  }

  async checkDocumentLink(url: string): Promise<DocumentLinkStatus> {
    return this.apiService.checkDocumentLink(url)
  }

  async saveDraft(settings: SettingsDocument): Promise<void> {
    return this.apiService.saveDraft(settings)
  }

  private theme: BrandTheme

  private get darkModeActive(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  private declarationsFor(scope: ResolvedScope): string {
    const colors = cssVariablesOf(scope)
    const declarations = Object.keys(colors)
      .filter(key => !!colors[key])
      .map(key => `  --${key}: ${colors[key]};`)

    return [...declarations, ...this.ionicDeclarations(colors)].join('\n')
  }

  /**
   * Ionic reads its palette from its own variables, so the branded colours have to be
   * mirrored into them for every mode.
   */
  private ionicDeclarations(colors: BrandColors): string[] {
    return [
      ...this.ionicPalette('primary', colors[CRBSettingKeys.CallToActionColor]),
      ...this.ionicPalette('secondary', colors[CRBSettingKeys.SecondaryFunctionColor]),
      ...this.ionicPalette('tertiary', colors[CRBSettingKeys.ModuleHeaderColor]),
    ]
  }

  private ionicPalette(name: string, color: string): string[] {
    if (!color) return []

    const rgb = this.hexToRgb(color)
    const contrast = this.getColorBrightness(color) < 138 ? '#ffffff' : '#000000'
    const contrastRGB = this.hexToRgb(contrast)

    return [
      `  --ion-color-${name}: ${color};`,
      `  --ion-color-${name}-rgb: ${rgb.r}, ${rgb.g}, ${rgb.b};`,
      `  --ion-color-${name}-contrast: ${contrast};`,
      `  --ion-color-${name}-contrast-rgb: ${contrastRGB.r}, ${contrastRGB.g}, ${contrastRGB.b};`,
      `  --ion-color-${name}-shade: ${this.darkenColor(color, 10)};`,
      `  --ion-color-${name}-tint: ${this.lightenColor(color, 10)};`,
    ]
  }

  private getColorBrightness(hexColor: string) {
    const rgb = this.hexToRgb(hexColor)

    if (!rgb) return 0

    const brightness = Math.round(((rgb.r * 299) +
      (rgb.g * 587) +
      (rgb.b * 114)) / 1000)

    return brightness
  }

  private hexToRgb(hex) {
    if (!hex) return null

    // Expand shorthand form (e.g. '03F') to full form (e.g. '0033FF')
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
    hex = hex.replace(shorthandRegex, function (r: number, g: number, b: number) {
      return r + r + g + g + b + b
    })

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  private darkenColor(color, amount) {
    const parsedColor = this.parseColor(color)
    const darkenedColor = {
      r: Math.max(parsedColor.r - amount, 0),
      g: Math.max(parsedColor.g - amount, 0),
      b: Math.max(parsedColor.b - amount, 0),
    }
    return this.formatColor(darkenedColor)
  }

  private lightenColor(color, amount) {
    const parsedColor = this.parseColor(color)
    const lightenedColor = {
      r: Math.min(parsedColor.r + amount, 255),
      g: Math.min(parsedColor.g + amount, 255),
      b: Math.min(parsedColor.b + amount, 255),
    }
    return this.formatColor(lightenedColor)
  }

  private parseColor(color) {
    const hex = color.replace(/^#/, '')
    const bigint = parseInt(hex, 16)
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    }
  }

  private formatColor(color) {
    const { r, g, b } = color
    return `rgb(${r}, ${g}, ${b})`
  }
}
